import { useLanguage } from './useLanguage';
import { useMemo } from 'react';
// From the `/groups` subpath, not the package root: the root pulls the whole
// registry — seventy-two definitions with their parameters — and none of it
// tree-shakes, which put 52kb in the browser bundle to render seven menu rows.
import { PATTERN_GROUPS } from '@moksy/patterns/groups';

/**
 * @file src/hooks/useDynamicCatalog.js
 * @description Source-of-truth for the dashboard's primary navigation.
 *
 *   Each item carries:
 *     - id          unique key for React reconciliation
 *     - label       short, sentence-case
 *     - path        target route (must exist in routes.jsx or componentMap)
 *     - componentId optional — links to componentMap when the route is
 *                   catalog-driven rather than statically declared
 *     - roles       which roles see the item
 *     - section     human-readable group header in the sidebar
 *     - icon        REQUIRED, every row at every depth — a key into the ICONS
 *                   map in navigation-drawer.snippet.jsx. Nav rows are scanned
 *                   by glyph before they're read, and the collapsed rail is
 *                   icon-only, so a row without one is a row users can't find.
 *                   An unknown/missing name degrades to a path heuristic, then
 *                   to a dot — it renders, it just stops being scannable.
 *     - children    nested rows (a group). A group row is a toggle, not a
 *                   link: it has no `path` and its children inherit its
 *                   `roles`. Nesting is recursive and unbounded, but keep it
 *                   to 3 levels — the rail is 260px and each level indents.
 *                   `minTier` / `feature` gates are only applied to TOP-LEVEL
 *                   rows (core/Sidebar.jsx), so never nest a gated row.
 *
 *   Section taxonomy (display order matters):
 *     1. Workspace      — what users actually do day-to-day
 *     2. Progression    — ladder, wallet, mentor relationships, perks
 *     3. Account        — profile, billing, history
 *     4. People         — admin: identity / auth / permissions
 *     5. Build          — admin: projects / requirements / preview / registry
 *     6. Knowledge      — admin: dictionary / storage
 *     7. AI Platform    — admin: AI llm / memory / policy
 *     8. Operations     — admin: audit / events / jobs / orchestrator
 *     9. Commerce       — admin: payments / billing
 *
 *   The dictionary service can override individual entries at runtime
 *   (overlay logic at the bottom of this file) but the baseline below
 *   is what the user sees if dictionary navigation isn't loaded yet.
 */

const SECTION = {
  OPERATOR:    'Operator console',
  WORKSPACE:   'Workspace',
  PROGRESSION: 'Progression',
  ACCOUNT:     'Account',
  PEOPLE:      'People',
  BUILD:       'Build',
  KNOWLEDGE:   'Knowledge',
  AI_PLATFORM: 'AI platform',
  OPERATIONS:  'Operations',
  COMMERCE:    'Commerce'
};

// Owner-group roles. Imported, not retyped.
//
// The dashboard carried SIX separate copies of this list — here, useRole.js,
// identity-menu, PublicProfile, useRedirectIfAuthed, and the persona module —
// each with a comment asking the next editor to keep the others in step. They
// agreed by luck. Missing one when a staff role is added does not fail a
// build or a test; it produces a staff member who cannot see their own
// console, which is the same silent shape as the role chart that hid 84 of 95
// accounts.
//
// Re-exported here because several screens already import `OWNER_ROLES` from
// this module; collapsing the copies should not also be a rename.
export { OWNER_ROLES } from '@moksy/common/src/permissions/ownerRoles.js';
import { OWNER_ROLES } from '@moksy/common/src/permissions/ownerRoles.js';
import { rolesForSurface } from '@moksy/common/src/permissions/desks.js';

const USER_TRACK_ROLES = ['user', 'developer', 'designer', 'content_writer', 'reviewer'];

// Worker-track-only roles — the subset of USER_TRACK_ROLES that can
// actually do reviewer work. Plain `user` is excluded because a fresh
// customer sign-up shouldn't see surfaces tied to reviewing (Reviewer
// dashboard, Expert review queue) until they've explicitly opted in
// to a worker track via an expert-application. The reviewer
// application surface itself stays on USER_TRACK_ROLES so plain users
// can still find their way INTO becoming a reviewer.
const WORKER_TRACK_ROLES = ['developer', 'designer', 'content_writer', 'reviewer'];

// Applicant roles — user-track accounts that are NOT yet reviewers. Used for
// the "Become a reviewer" entry so plain customers can discover the
// application (which lives at /reviewer). Existing reviewers see the
// "Reviewer dashboard" entry instead, so the two never double-list.
const APPLICANT_ROLES = USER_TRACK_ROLES.filter((r) => r !== 'reviewer');

// All authenticated roles — owner-group + user-group. Use this for rows
// where an operator legitimately needs the same view as a customer
// (e.g. Projects, AI requirements — operators inspect them during
// support sessions). For pure customer surfaces (wallet, subscription,
// pillars, etc.) prefer USER_TRACK_ROLES.
const ALL_AUTH_ROLES = [...USER_TRACK_ROLES, ...OWNER_ROLES];

// Operator rows do not list roles. They say `roles: BY_DESK`, and the pass at
// the bottom of this file replaces it with the owner roles whose DESKS work
// that surface (packages/common/src/permissions/desks.js).
//
// Until 2026-09-11 thirty-eight operator rows said `roles: OWNER_ROLES`, so a
// front-line support agent's sidebar was an admin's: Permissions, Captcha, the
// API gateway, AI models, Billing, Subscriptions. A role list per row cannot
// fix that without becoming thirty-eight hand lists that drift from the
// gateway; one surface → desk table, read here AND by the gateway's
// `requireDesk`, can. A group's children each resolve on their own path, so a
// group shows only the rows its reader may open.
const BY_DESK = 'by-desk';

export const SECTION_ORDER = [
  SECTION.OPERATOR,
  SECTION.WORKSPACE,
  SECTION.PROGRESSION,
  SECTION.ACCOUNT,
  SECTION.PEOPLE,
  SECTION.BUILD,
  SECTION.KNOWLEDGE,
  SECTION.AI_PLATFORM,
  SECTION.OPERATIONS,
  SECTION.COMMERCE
];

const DECLARED_CATALOG_ITEMS = [
  // ── Operator console (owner-group governance) ────────────────────
  // Each row is shown to the owner roles whose desks work it (`BY_DESK`,
  // resolved at the bottom of this file); admin and super_admin see all of
  // it. Per-action gating (e.g. who can save a matrix cell) still lives
  // inside each page using scope checks.
  // The console row is the section root — at-a-glance metrics plus
  // launchpad links to every leaf below.
  //
  // Templates / Knowledge Base / Journey / Learning below are the CONTENT
  // model — the conceptual layers of the requirements engine. The rows here
  // were the other fourteen: every operator surface ever built, flat, in the
  // order it shipped. They're grouped by job now (engine admin / reviews /
  // people), so the section is scannable and a new surface has an obvious
  // home instead of landing at the bottom of a list.
  { id: 'op-console',     section: SECTION.OPERATOR, label: 'Console',             path: '/admin/console',            componentId: 'OperatorConsole',   roles: BY_DESK, icon: 'gauge' },
  { id: 'op-reports',     section: SECTION.OPERATOR, label: 'Report Manager',      path: '/admin/reports',            componentId: 'ReportManager',     roles: BY_DESK, icon: 'fileBarChart' },

  // Engine admin — running the requirements service itself: KPI snapshot,
  // feature-flag rollouts, audit trail. Not an authoring step, which is why
  // it's a peer of the content layers rather than part of them.
  {
    id: 'op-engine-admin', section: SECTION.OPERATOR, label: 'Engine admin', roles: BY_DESK, icon: 'cog',
    children: [
      { id: 'op-reqv2-admin', label: 'Overview',      path: '/requirements/v2/admin',               componentId: 'RequirementsV2AdminHome',    icon: 'gauge' },
      { id: 'op-reqv2-flags', label: 'Feature flags', path: '/requirements/v2/admin/feature-flags', componentId: 'RequirementsV2FeatureFlags', icon: 'flag' },
      { id: 'op-reqv2-audit', label: 'Audit log',     path: '/requirements/v2/admin/audit-log',     componentId: 'RequirementsV2AuditLog',     icon: 'clipboardList' }
    ]
  },
  // Content hierarchy — a real nested tree: Templates > {Base, Master,
  // Category based templates}, each exposing the same facets
  // (structure, questions with default answers + weights, solution
  // report). `children` items are plain {id, label, path} nodes with
  // no section/roles of their own (visibility is inherited from this
  // root item) — NavigationDrawer renders any item carrying `children`
  // as a collapsible node, recursively, however deep.
  //
  // "Category based templates" is a plain link (not an expandable
  // group) straight to the list/edit page — selecting an individual
  // category and amending its questions/answers/weights + a read-only
  // reference view of the shared pillar structure all happens inside
  // that one page (DocumentCategoryTemplatesPage), not via per-category
  // sidebar sub-nodes. Categories are data (added without a deploy), so
  // a per-category nav entry would need a live fetch just to render the
  // sidebar — not worth it when the target page already does the job.
  //
  // Pillars/Sub-pillars are the same canonical structure under every
  // tier — there's only one, owned by Base; Master/Category overlay
  // VALUES on top of it (see MasterTemplatePage / DocumentCategory-
  // TemplatesPage). The links under Master/Category point at the same
  // pages as Base's — shortcuts for context, not separate datasets.
  {
    id: 'op-reqv2-templates', section: SECTION.OPERATOR, label: 'Templates', roles: BY_DESK, icon: 'layers',
    children: [
      // Workflow templates sit ABOVE the three content tiers because they are
      // a different axis, not a fourth tier. Base/Master/Category overlay
      // VALUES onto one shared pillar tree; a workflow template owns its own
      // STEP tree, with a function and parameter values bound per step. Filing
      // it as a peer of Base would suggest a project picks one of four, which
      // is exactly backwards: a project resolves a content tier AND runs a
      // workflow.
      // The named workflow templates — London, Milan, Dhaka, Newton — and the
      // two granularities the model declares (`SCOPES = ['journey','step']`):
      // a journey template owns a whole workflow, a step template a single
      // reusable step. Counts come from the same listing the page reads.
      { id: 'tpl-workflows',   label: 'All templates',     path: '/templates',                icon: 'workflow' },
      { id: 'tpl-scope-journey', label: 'Journey templates', path: '/templates?scope=journey', countKey: 'journey', icon: 'map' },
      { id: 'tpl-scope-step',    label: 'Step templates',    path: '/templates?scope=step',    countKey: 'step',    icon: 'listChecks' },
      {
        id: 'tpl-base', label: 'Base template', icon: 'boxes',
        children: [
          { id: 'tpl-base-pillars',    label: 'Pillars',             path: '/requirements/v2/admin/pillars',                  icon: 'columns' },
          { id: 'tpl-base-horizontal', label: 'Horizontal pillars',  path: '/requirements/v2/admin/pillars?scope=horizontal', icon: 'rows' },
          { id: 'tpl-base-questions',  label: 'Questions & weights', path: '/requirements/v2/admin/base-template',            icon: 'helpCircle' },
          { id: 'tpl-base-factors',    label: 'Factor weights',      path: '/requirements/v2/admin/factor-weights',           icon: 'sliders' },
          { id: 'tpl-base-structure',  label: 'Report structure',    path: '/requirements/v2/admin/report-structure',         icon: 'layoutTemplate' },
          { id: 'tpl-base-report',     label: 'Solution report',     path: '/requirements/v2/admin/base-solution-document',   icon: 'fileText' },
          { id: 'tpl-base-format',     label: 'Report format',       path: '/requirements/v2/admin/report-format',            icon: 'palette' }
        ]
      },
      {
        // "Sub-pillars" had its own row here pointing at the SAME URL as
        // Pillars — two rows, one page, both highlighting together. The
        // Pillars page edits both levels, so the duplicate is gone.
        id: 'tpl-master', label: 'Master template', icon: 'star',
        children: [
          { id: 'tpl-master-pillars',    label: 'Pillars',             path: '/requirements/v2/admin/pillars?templateCode=master',                  icon: 'columns' },
          { id: 'tpl-master-horizontal', label: 'Horizontal pillars',  path: '/requirements/v2/admin/pillars?scope=horizontal&templateCode=master', icon: 'rows' },
          { id: 'tpl-master-questions',  label: 'Questions & weights', path: '/requirements/v2/admin/master-template',                              icon: 'helpCircle' },
          { id: 'tpl-master-report',     label: 'Solution report',     path: '/requirements/v2/admin/master-solution-document',                     icon: 'fileText' }
        ]
      },
      {
        id: 'tpl-category', label: 'Category master template', icon: 'tag',
        children: [
          { id: 'tpl-cat-list',      label: 'Categories',          path: '/requirements/v2/admin/document-category-templates', icon: 'table' },
          { id: 'tpl-cat-pillars',   label: 'Pillars',             path: '/requirements/v2/admin/pillars?templateCode=category', icon: 'columns' },
          // Same page as Categories above — two facets of one editor, which is
          // how the tier was authored. Both rows highlight together as a result.
          { id: 'tpl-cat-questions', label: 'Questions & weights', path: '/requirements/v2/admin/document-category-templates', icon: 'helpCircle' },
          { id: 'tpl-cat-report',    label: 'Solution report',     path: '/requirements/v2/admin/category-solution-document',  icon: 'fileText' }
        ]
      },
      // Peer of the three tiers, not a child of one: a proposal travels
      // category → master → base, so it belongs to the pipeline between them.
      { id: 'tpl-proposals', label: 'Proposals', path: '/requirements/v2/admin/template-proposals', icon: 'inbox' }
    ]
  },

  // ── Functions ────────────────────────────────────────────────────
  //
  // The whole set of actions, of which Patterns is the half that decides
  // rather than executes. A direct link because there is one screen: giving it
  // a toggle with a single child would add a click and imply a structure the
  // registry does not have.
  { id: 'op-functions', section: SECTION.OPERATOR, label: 'Functions', path: '/functions', roles: BY_DESK, icon: 'zap' },

  // ── Patterns ─────────────────────────────────────────────────────
  //
  // Its own section, above Knowledge Base, because a pattern ACTS ON knowledge
  // rather than being a kind of it — and because this is where the behaviour
  // of every journey is actually tuned. Filing it under Templates or Engine
  // admin would bury the thing an expert opens most often.
  //
  // The rows below are GENERATED from `PATTERN_GROUPS` rather than written
  // out. Twenty-six families with thirteen of them holding a single pattern is
  // a useful key and a useless menu, so the sidebar shows the seven stages
  // they sort into — the same ordering the resolution cascade runs in. Writing
  // them by hand would create a second taxonomy that drifts from the first,
  // which is precisely how this platform ended up with three tool catalogues
  // that were one table.
  {
    id: 'op-patterns', section: SECTION.OPERATOR, label: 'Patterns', roles: BY_DESK, icon: 'braces',
    children: [
      // The unfiltered catalogue stays first: a group is a narrowing, and the
      // thing being narrowed has to be reachable in one click.
      { id: 'patterns-all', label: 'All patterns', path: '/patterns', icon: 'table' },
      ...[...PATTERN_GROUPS].sort((a, b) => a.order - b.order).map((g) => ({
        id: `patterns-${g.code}`,
        label: g.label,
        path: `/patterns?group=${g.code}`,
        icon: g.icon
      }))
    ]
  },

  // ── Schemas ──────────────────────────────────────────────────────
  //
  // Its own section, above Knowledge Base, for the same reason Patterns sits
  // above it: a schema decides what a record CAN hold, so it is the question
  // before the corpus rather than a page inside it. Filed under Knowledge Base
  // it read as one more list of records, which is the one thing it is not.
  {
    id: 'op-schemas', section: SECTION.OPERATOR, label: 'Schemas', roles: BY_DESK, icon: 'boxes',
    // The five categories, not two pages.
    //
    // "All schemas" and "Project fields" told nobody what the platform holds;
    // the categories are the level somebody navigates at, and their counts are
    // the fastest read of the estate anywhere in the product. `countKey` names
    // the group whose live total the sidebar fills in — the numbers are the
    // service's, fetched once, and the row renders without one rather than
    // guessing when the fetch has not landed.
    children: [
      { id: 'schemas-people',  label: 'People',  path: '/knowledge/schemas?group=people',  countKey: 'people',  icon: 'users' },
      { id: 'schemas-company', label: 'Company', path: '/knowledge/schemas?group=company', countKey: 'company', icon: 'shoppingBag' },
      { id: 'schemas-product', label: 'Product', path: '/knowledge/schemas?group=product', countKey: 'product', icon: 'package' },
      { id: 'schemas-service', label: 'Service', path: '/knowledge/schemas?group=service', countKey: 'service', icon: 'headset' },
      { id: 'schemas-others',  label: 'Others',  path: '/knowledge/schemas?group=others',  countKey: 'others',  icon: 'boxes' },
      { id: 'schemas-all', label: 'All schemas', path: '/knowledge/schemas', icon: 'table' },
      // The one schema that can change without a deploy. Beside the catalogue
      // rather than inside it, because the catalogue is a read-only reflection
      // of code and this is the opposite: a declaration that changes behaviour.
      { id: 'schemas-fields', label: 'Project fields', path: '/knowledge/project-fields', icon: 'columns' }
    ]
  },

  // ── Configurations ───────────────────────────────────────────────
  //
  // What is switched ON, as opposed to what exists. Templates, Actions and
  // Schemas all describe things that COULD run; this is the one section that
  // decides which of them a customer actually meets, so it is not a page
  // inside any of them.
  {
    id: 'op-configurations', section: SECTION.OPERATOR, label: 'Configurations', roles: BY_DESK, icon: 'sliders',
    children: [
      // Was "Workflow templates" under Journey, which put TWO menu entries of
      // that name in the sidebar pointing at different objects: the named
      // workflows that own a step tree, and these — fifteen numbers tuning a
      // tree they do not own. Renamed to what the Configurations column
      // already calls it, and moved beside the screen that selects them.
      { id: 'cfg-workflow', label: 'All configuration', path: '/configurations', icon: 'route' },
      // What GOVERNS each action, from the registry's own CONTROL_KINDS. The
      // index counts 380 actions and 216 of them are hard-coded — not a defect,
      // the honest size of the configurable surface, and a fact nothing showed
      // until this row existed. Hard-coded is listed for exactly that reason:
      // knowing what you cannot change without a deploy is the point.
      { id: 'cfg-k-policy',     label: 'Workflow policy', path: '/configurations?kind=policy',     countKey: 'policy',     icon: 'sliders' },
      { id: 'cfg-k-template',   label: 'Template',        path: '/configurations?kind=template',   countKey: 'template',   icon: 'layoutTemplate' },
      { id: 'cfg-k-setting',    label: 'Setting',         path: '/configurations?kind=setting',    countKey: 'setting',    icon: 'cog' },
      { id: 'cfg-k-data',       label: 'Operator data',   path: '/configurations?kind=data',       countKey: 'data',       icon: 'database' },
      { id: 'cfg-k-plan',       label: 'Plan',            path: '/configurations?kind=plan',       countKey: 'plan',       icon: 'creditCard' },
      { id: 'cfg-k-permission', label: 'Permission',      path: '/configurations?kind=permission', countKey: 'permission', icon: 'key' },
      { id: 'cfg-k-code',       label: 'Hard-coded',      path: '/configurations?kind=code',       countKey: 'code',       icon: 'braces' },
      { id: 'cfg-k-unverified', label: 'Unverified',      path: '/configurations?kind=unverified', countKey: 'unverified', icon: 'helpCircle' },
      { id: 'cfg-policies', label: 'Run policies', path: '/requirements/v2/admin/workflow-templates', icon: 'workflow' }
    ]
  },

  // ── Knowledge Base ───────────────────────────────────────────────
  // The universal pool of defaults and building blocks that ANY project could
  // draw on. Base Template is the weighted, structured SELECTION from it — so
  // it is a sibling of Templates, not a child of one. Was previously buried
  // inside Templates as "Data Management", which conflated the pool with the
  // selection.
  {
    id: 'op-knowledge-base', section: SECTION.OPERATOR, label: 'Data', roles: BY_DESK, icon: 'database',
    children: [
      // The corpus — technologies, modules, protocols, roles, lifecycle
      // phases. Listed first because it is the material the two screens below
      // draw on: a default cites a record here, and an example is judged
      // against them.
      //
      // ── Why the types are grouped rather than listed flat ───────────
      //
      // Twenty-seven entity types in one menu is a list nobody reads. Grouped
      // by what somebody is actually doing — choosing a stack, describing a
      // capability, staffing a team, scoping a client — each group is two to
      // six items, which is a menu.
      //
      // The grouping is PRESENTATION only. The tiers that matter structurally
      // (catalog / structure / mirror) live in the registry and are shown on
      // the Corpus overview; repeating them here would organise the menu
      // around our data model rather than around the work.
      { id: 'data-home', label: 'All data', path: '/knowledge/data', icon: 'database' },
      { id: 'data-discovery',      label: 'Discovery',            path: '/knowledge/data?domain=discovery',      countKey: 'discovery',      icon: 'eye' },
      { id: 'data-architecture',   label: 'Architecture',         path: '/knowledge/data?domain=architecture',   countKey: 'architecture',   icon: 'gitBranch' },
      { id: 'data-design',         label: 'Design & UX',          path: '/knowledge/data?domain=design',         countKey: 'design',         icon: 'palette' },
      { id: 'data-frontend',       label: 'Frontend',             path: '/knowledge/data?domain=frontend',       countKey: 'frontend',       icon: 'layoutTemplate' },
      { id: 'data-backend',        label: 'Backend',              path: '/knowledge/data?domain=backend',        countKey: 'backend',        icon: 'server' },
      { id: 'data-database',       label: 'Database',             path: '/knowledge/data?domain=database',       countKey: 'database',       icon: 'database' },
      { id: 'data-integration',    label: 'Integration',          path: '/knowledge/data?domain=integration',    countKey: 'integration',    icon: 'plug' },
      { id: 'data-testing',        label: 'Testing',              path: '/knowledge/data?domain=testing',        countKey: 'testing',        icon: 'flask' },
      { id: 'data-security',       label: 'Security',             path: '/knowledge/data?domain=security',       countKey: 'security',       icon: 'shieldCheck' },
      { id: 'data-server',         label: 'Server & Deployment',  path: '/knowledge/data?domain=server',         countKey: 'server',         icon: 'cpu' },
      { id: 'data-observability',  label: 'Observability',        path: '/knowledge/data?domain=observability',  countKey: 'observability',  icon: 'activity' },
      { id: 'data-scaling',        label: 'Scaling',              path: '/knowledge/data?domain=scaling',        countKey: 'scaling',        icon: 'trendingUp' },
      { id: 'data-content',        label: 'Content',              path: '/knowledge/data?domain=content',        countKey: 'content',        icon: 'fileText' },
      { id: 'data-sales',          label: 'Sales',                path: '/knowledge/data?domain=sales',          countKey: 'sales',          icon: 'headset' },
      { id: 'data-marketing',      label: 'Marketing',            path: '/knowledge/data?domain=marketing',      countKey: 'marketing',      icon: 'trendingUp' },
      { id: 'data-maintenance',    label: 'Maintenance',          path: '/knowledge/data?domain=maintenance',    countKey: 'maintenance',    icon: 'settings' },
      { id: 'data-governance',     label: 'Governance',           path: '/knowledge/data?domain=governance',     countKey: 'governance',     icon: 'gavel' },
      { id: 'data-delivery',       label: 'Delivery & Planning',  path: '/knowledge/data?domain=delivery',       countKey: 'delivery',       icon: 'calendarClock' },
      { id: 'data-commercial',     label: 'Commercial',           path: '/knowledge/data?domain=commercial',     countKey: 'commercial',     icon: 'banknote' },
      { id: 'data-people',         label: 'People',               path: '/knowledge/data?domain=people',         countKey: 'people',         icon: 'users' },
      { id: 'data-market',         label: 'Market',               path: '/knowledge/data?domain=market',         countKey: 'market',         icon: 'tag' },
      { id: 'data-structure',      label: 'Report structure',     path: '/knowledge/data?domain=structure',      countKey: 'structure',      icon: 'layers' },
      { id: 'data-guidance',       label: 'Guidance',             path: '/knowledge/data?domain=guidance',       countKey: 'guidance',       icon: 'messageSquare' },
      // Kept below the domains: this lists the twenty-three TYPES and their
      // fill rates, which is the registry's view of the corpus rather than the
      // lifecycle's. Both are true; only one of them is what somebody arrives
      // asking for.
      { id: 'kb-corpus', label: 'Corpus overview', path: '/knowledge/corpus', icon: 'bookOpen' },
      {
        id: 'kb-stack', label: 'Stack', icon: 'layers',
        children: [
          { id: 'kb-technology', label: 'Technologies', path: '/knowledge/type/technology', icon: 'braces' },
          { id: 'kb-tool', label: 'Tools', path: '/knowledge/type/tool', icon: 'settings' },
          { id: 'kb-ai-tool', label: 'AI tools', path: '/knowledge/type/ai_tool', icon: 'sparkles' },
          { id: 'kb-product', label: 'Products', path: '/knowledge/type/product', icon: 'package' },
          // What you could build ON instead of building — 127 of them, imported
          // from the requirements service's platform blocks. Beside Products
          // because the decision is adjacent: buy it, or build on it.
          { id: 'kb-readymade', label: 'Readymade solutions', path: '/knowledge/type/readymade_solution', icon: 'boxes' },
          { id: 'kb-service', label: 'Services', path: '/knowledge/type/service_offering', icon: 'headset' },
          { id: 'kb-company', label: 'Companies', path: '/knowledge/type/company', icon: 'shoppingBag' }
        ]
      },
      {
        id: 'kb-capability', label: 'Capability', icon: 'boxes',
        children: [
          { id: 'kb-module', label: 'Modules', path: '/knowledge/type/module', icon: 'boxes' },
          { id: 'kb-protocol', label: 'Protocols & standards', path: '/knowledge/type/protocol', icon: 'shieldCheck' },
          { id: 'kb-framework', label: 'Frameworks', path: '/knowledge/type/framework', icon: 'layers' },
          { id: 'kb-lifecycle', label: 'Lifecycle phases', path: '/knowledge/type/lifecycle_phase', icon: 'activity' }
        ]
      },
      {
        // The document tree, imported from the requirements service and kept
        // in step by re-running the import. Grouped apart from Capability
        // because these describe where material GOES, not what it is.
        id: 'kb-tree', label: 'Document tree', icon: 'workflow',
        children: [
          { id: 'kb-pillar', label: 'Pillars', path: '/knowledge/type/pillar', icon: 'columns' },
          { id: 'kb-sub-pillar', label: 'Sub-pillars', path: '/knowledge/type/sub_pillar', icon: 'rows' },
          { id: 'kb-horizontal', label: 'Horizontal pillars', path: '/knowledge/type/horizontal_pillar', icon: 'table' },
          // Questions sit with the tree they belong to, not under Mirrored:
          // they carry their pillar, sub-pillar and lens, so they are sortable
          // by section or by lens rather than being a pointer.
          { id: 'kb-question', label: 'Questions', path: '/knowledge/type/question', icon: 'helpCircle' }
        ]
      },
      {
        id: 'kb-people', label: 'People', icon: 'users',
        children: [
          { id: 'kb-role', label: 'Roles', path: '/knowledge/type/role', icon: 'users' },
          { id: 'kb-skill', label: 'Skills', path: '/knowledge/type/skill', icon: 'star' },
          { id: 'kb-skill-level', label: 'Skill levels', path: '/knowledge/type/skill_level', icon: 'scale' },
          { id: 'kb-certification', label: 'Certifications', path: '/knowledge/type/certification', icon: 'award' },
          { id: 'kb-accreditation', label: 'Accreditations', path: '/knowledge/type/accreditation', icon: 'award' },
          { id: 'kb-education', label: 'Education', path: '/knowledge/type/education', icon: 'bookOpen' }
        ]
      },
      {
        id: 'kb-context', label: 'Context', icon: 'tag',
        children: [
          { id: 'kb-app-category', label: 'Application categories', path: '/knowledge/type/application_category', icon: 'columns' },
          { id: 'kb-app-type', label: 'Application types', path: '/knowledge/type/application_type', icon: 'layoutTemplate' },
          { id: 'kb-size-band', label: 'Size bands', path: '/knowledge/type/size_band', icon: 'gauge' },
          { id: 'kb-sector', label: 'Sectors', path: '/knowledge/type/sector', icon: 'tag' },
          { id: 'kb-industry', label: 'Industries', path: '/knowledge/type/industry', icon: 'layers' },
          { id: 'kb-sub-industry', label: 'Sub-industries', path: '/knowledge/type/sub_industry', icon: 'layers' }
        ]
      },
      {
        id: 'kb-guidance', label: 'Guidance', icon: 'messageSquare',
        children: [
          { id: 'kb-prompt', label: 'Prompts', path: '/knowledge/type/prompt', icon: 'messageSquare' },
          { id: 'kb-tip', label: 'Tips', path: '/knowledge/type/tip', icon: 'helpCircle' }
        ]
      },
      {
        // Mirrors are grouped apart because they behave differently: they
        // point at records another service owns and cannot be authored here.
        id: 'kb-mirrors', label: 'Mirrored', icon: 'link',
        children: [
          { id: 'kb-journey', label: 'Journeys', path: '/knowledge/type/journey', icon: 'map' },
          { id: 'kb-report-type', label: 'Report types', path: '/knowledge/type/report_type', icon: 'fileText' }
        ]
      },
      { id: 'kb-defaults', label: 'Question defaults & blocks', path: '/requirements/v2/admin/knowledge-base', icon: 'database' },
      // Examples live beside the knowledge that feeds generation rather than
      // under marketing, because a training-approved example steers future
      // reports through the retrieval bank. Filing it as content would put
      // the decision that changes generation in the place people go to edit
      // copy.
      // What each report type PRODUCES — its sections, in order, drawn from the
      // document tree. Replaces Examples in this position: that page answered
      // "which report types have a sample artefact", which reads as twenty rows
      // of "None" until artefacts exist and never showed what a report type is.
      { id: 'kb-outputs', label: 'Output reports', path: '/knowledge/output-reports', icon: 'fileText' },
      // Kept, one level down: the example library is a real feature with its
      // own coverage model, it is simply not what this slot was being asked.
      { id: 'kb-examples', label: 'Example library', path: '/requirements/v2/admin/examples', icon: 'layers' }
    ]
  },

  // ── Journey ──────────────────────────────────────────────────────
  // The map first, then the two RUNTIME concerns that decide which engine +
  // template + policy a live project resolves to. Neither of those is an
  // authoring step, which is why they no longer sit under Base template.
  //
  // Journeys leads because it is the one that answers "what is this platform
  // meant to do, and what does it do today" — the question the other two are
  // read in service of. It also had no entry at all until now: the page was
  // reachable only by typing the URL, which is the same missing-surface
  // failure the map itself exists to record.
  {
    id: 'op-journey', section: SECTION.OPERATOR, label: 'Journeys', roles: BY_DESK, icon: 'gitBranch',
    // The four kinds the registry declares, in the order the registry declares
    // them — the same order the console's own boxes render in, because the
    // boxes read JOURNEY_KINDS straight from the service. Two orders for one
    // feature reads as a bug even when both are defensible.
    //
    // Counts come from the same list the console reads, so the menu cannot
    // claim a journey the page has not got — see useJourneyCounts.
    children: [
      { id: 'jr-journeys',  label: 'All journeys',       path: '/requirements/v2/admin/journeys',                 icon: 'map' },
      { id: 'jr-role',      label: 'Role',               path: '/requirements/v2/admin/journeys?kind=role',       countKey: 'role',     icon: 'users' },
      { id: 'jr-activity',  label: 'Activity',           path: '/requirements/v2/admin/journeys?kind=activity',   countKey: 'activity', icon: 'zap' },
      { id: 'jr-pipeline',  label: 'Pipeline',           path: '/requirements/v2/admin/journeys?kind=pipeline',   countKey: 'pipeline', icon: 'route' },
      { id: 'jr-service',   label: 'Service',            path: '/requirements/v2/admin/journeys?kind=service',    countKey: 'service',  icon: 'server' },
      { id: 'jr-version',   label: 'Workflow version',   path: '/requirements/v2/admin/workflow-version',         icon: 'gitCompare' }
    ]
  },

  // ── Learning ─────────────────────────────────────────────────────
  //
  // The loop that makes the platform better over time, in one section.
  //
  // This was called "Evidence", and its own comment described it as "what
  // Learning reasons OVER" — so the menu showed the loop's INPUTS, under a
  // name that was not the loop's, and the loop's conclusions had no entry at
  // all. A nightly job had written twenty thousand metric snapshots that no
  // screen could reach.
  //
  // Ordered as the loop runs, not alphabetically: what it concluded, what it
  // wants to change, how a judgement is made, what it reasoned over, and what
  // the model is shown as a result. Reading top to bottom is reading the loop.
  {
    id: 'op-learning', section: SECTION.OPERATOR, label: 'Learning', roles: BY_DESK, icon: 'brain',
    children: [
      // FIRST, because it is the loop's only missing input. Metrics computed
      // from eleven ratings, zero proposal clusters from four corrections and
      // no gold at all are all the same fact: nothing has been judged. Putting
      // the read-outs above the one action that moves them had the section
      // reading as a set of dashboards rather than a job of work.
      // The list first: 95 runs, and until it existed the only way in was the
      // judging queue, which shows one unjudged run at a time. The four states
      // ARE the loop — collect, verify, approve, amend — so they are the rows.
      { id: 'ln-runs',       label: 'All runs',          path: '/learning/runs',                           icon: 'history' },
      { id: 'ln-s-unjudged', label: 'Awaiting judgement', path: '/learning/runs?state=unjudged',  countKey: 'unjudged',  icon: 'inbox' },
      { id: 'ln-s-judged',   label: 'Judged',             path: '/learning/runs?state=judged',    countKey: 'judged',    icon: 'clipboardCheck' },
      { id: 'ln-s-corrected', label: 'Corrected',         path: '/learning/runs?state=corrected', countKey: 'corrected', icon: 'eraser' },
      { id: 'ln-s-failed',   label: 'Failed',             path: '/learning/runs?state=failed',    countKey: 'failed',    icon: 'alertOctagon' },
      { id: 'ln-judge',      label: 'Judge runs',        path: '/learning/judge',                          icon: 'clipboardCheck' },
      { id: 'ln-metrics',    label: 'Metrics',           path: '/learning/metrics',                        icon: 'trendingUp' },
      // The read side of the judgement link — what reviewers keep telling us
      // we got wrong, and which of our own mechanisms produced it.
      { id: 'ln-wrong',      label: 'What we get wrong', path: '/requirements/v2/admin/who-was-wrong',      icon: 'alertTriangle' },
      { id: 'ln-clusters',   label: 'Proposal clusters', path: '/requirements/v2/admin/proposal-clusters',  icon: 'gitCompare' },
      // Where gold is created: approving corrections on a corpus run is what
      // gives the loop something to score and retrieve against.
      { id: 'ln-review',     label: 'Corpus review',     path: '/requirements/v2/admin/review',             icon: 'clipboardCheck' },
      { id: 'ln-experiment', label: 'Experiment',        path: '/requirements/v2/admin/experiment',         icon: 'flask' },
      // `ln-runs` a second time — the id of "All runs" ten rows up. Ids key
      // the dictionary overlay, so two rows sharing one meant an override
      // written for either landed on both. Renamed to what it is.
      { id: 'ln-experiment-runs', label: 'Experiment runs', path: '/requirements/v2/admin/experiment-runs', icon: 'history' },
      { id: 'ln-retrieval',  label: 'Retrieval preview', path: '/learning/retrieval',                       icon: 'sparkles' }
    ]
  },

  // Reviews & experts — the whole marketplace supply side in one place: the
  // cross-tenant job pipeline (NOT the per-reviewer queue, which is a workspace
  // surface), the policy knobs (kill-switch, claim caps, earnings ceilings —
  // read-only for most operators, super-admin to mutate), contested-review
  // adjudication, the applicant queue, and what we owe reviewers.
  {
    id: 'op-reviews', section: SECTION.OPERATOR, label: 'Reviews & experts', roles: BY_DESK, icon: 'gavel',
    children: [
      { id: 'op-pipeline',       label: 'Review pipeline',     path: '/admin/review-pipeline',                     componentId: 'ReviewPipeline',                   icon: 'listChecks' },
      { id: 'op-review-policy',  label: 'Review policy',       path: '/admin/reviews/settings',                    componentId: 'ReviewPolicy',                     icon: 'shieldFile' },
      { id: 'op-reqv2-disputes', label: 'Review disputes',     path: '/requirements/v2/admin/disputes',            componentId: 'RequirementsV2Disputes',           icon: 'alertTriangle' },
      // "Expert applications" and "Reviewer applications" sat next to each
      // other as siblings for two entirely different funnels, and nothing on
      // either screen said which was which. This one grants a TRACK at a
      // level (backend L7); the rail below is the RV0–RV6 accreditation
      // ladder. Naming them after what they grant is the only version of
      // this list somebody can read cold.
      { id: 'op-reqv2-experts',  label: 'Track applications',  path: '/requirements/v2/admin/expert-applications', componentId: 'RequirementsV2ExpertApplications',  icon: 'award' },
      { id: 'op-earnings',       label: 'Reviewer earnings',   path: '/admin/reviewer-earnings',                   componentId: 'ReviewerEarnings',                 icon: 'coins' },
      { id: 'op-commons',        label: 'Commons windows',     path: '/admin/commons',                             componentId: 'CommonsConsole',                   icon: 'coins' },
      // 🎖️ Commission, concurrency and payout hold — the commercial terms
      // of the supply side. Seeded in code until now, so retuning them
      // meant a deploy.
      { id: 'op-reviewer-plans', label: 'Reviewer plans',      path: '/admin/reviewer-plans',                      componentId: 'ReviewerPlansAdmin',               icon: 'award' },
      // 📄 Calibration seeds and the reviewer capacity ratio — the gate
      // that decides WHO becomes a reviewer, and the ratio that decides
      // HOW MANY. Both were built with no screen, so both were off.
      { id: 'op-calibration',    label: 'Calibration & capacity', path: '/admin/calibration',                     componentId: 'CalibrationSeedsAdmin',            icon: 'clipboardCheck' },
      // 🚪 TWO MENU ITEMS, ONE PAGE (GC-108).
      //
      // This row and the one above were both `id: 'op-calibration'` pointing
      // at `/admin/calibration`, and routes.jsx declared that path twice.
      // React Router takes the first match, so "Calibration" and "Calibration
      // & capacity" opened the SAME screen — and the appeals console, the one
      // place a candidate's contested score can be answered, was unreachable
      // from anywhere in the product. Distinct path, distinct id, and a label
      // naming what only this screen does.
      { id: 'op-calibration-appeals', label: 'Calibration appeals', path: '/admin/calibration/appeals',           componentId: 'CalibrationAdmin',                 icon: 'flag' },
      // Held attempts wait on a person; with no screen they waited on nobody.
      { id: 'op-calibration-held',    label: 'Held calibrations',   path: '/admin/calibration/held',              componentId: 'CalibrationHeldAdmin',             icon: 'clock' },
      // Promotion runs automatically, which means it runs invisibly — this
      // is the only place an operator can see it happening.
      { id: 'op-promotions',     label: 'Reviewer promotions', path: '/admin/reviewer-promotions',                 componentId: 'ReviewerPromotions',               icon: 'trendingUp' },
      // Every sales, support, partner and job enquiry, with an owner and a
      // state. The four doors were `mailto:` links with no record at all.
      { id: 'op-enquiries',      label: 'Enquiries',           path: '/admin/enquiries',                           componentId: 'EnquiriesQueue',                   icon: 'inbox' },
      // The RV0–RV6 rail. Stage decisions had no UI at all, so candidates
      // sat in `awaiting_us` with nobody able to act on them.
      { id: 'op-rail',           label: 'Accreditation rail',  path: '/admin/reviewer-applications',               componentId: 'ReviewerRailAdmin',                icon: 'route' }
    ]
  },

  // 👥 ONE HEADING FOR PEOPLE (GC-094).
  //
  // The operator sidebar carried a group called "People & access" AND a
  // separate top-level section called "People", four rows apart, with no rule
  // an operator could infer for which held what — the split was "per-account
  // tools" versus "policy-tuning surfaces", a distinction that exists in this
  // file's comments and nowhere on the screen. Walked as staff: I looked for
  // Permissions under People & access, did not find it, and assumed it was
  // not built.
  //
  // Auth, Permissions and Captcha move in here. They are top-level rows only
  // in the sense that nothing gates them beyond OWNER_ROLES — no `minTier` or
  // `feature`, which is the one thing nesting would drop (see the note at the
  // top of this file).
  {
    id: 'op-people', section: SECTION.OPERATOR, label: 'People & access', roles: BY_DESK, icon: 'users',
    children: [
      { id: 'op-registry',    label: 'User registry',      path: '/admin/identity',           componentId: 'IdentityAdmin',       icon: 'usersSearch' },
      { id: 'op-users-admin', label: 'Users service',      path: '/admin/users-admin',        componentId: 'UsersAdminDashboard', icon: 'server' },
      { id: 'op-eligibility', label: 'Eligibility matrix', path: '/admin/eligibility-matrix', componentId: 'EligibilityMatrix',   icon: 'table' },
      { id: 'op-weights',     label: 'Scoring weights',    path: '/admin/scoring-weights',    componentId: 'ScoringWeights',      icon: 'sliders' },
      { id: 'admin-auth',        label: 'Auth',        path: '/admin/auth/control', componentId: 'AuthControl',      icon: 'key' },
      { id: 'admin-permissions', label: 'Permissions', path: '/admin/permissions',  componentId: 'PermissionsAdmin', icon: 'shieldCheck' },
      { id: 'admin-captcha',     label: 'Captcha',     path: '/admin/captcha',      componentId: 'CaptchaSettings',  icon: 'shieldCheck' }
    ]
  },

  // ── Workspace (user-side day-to-day) ─────────────────────────────
  // EVERY workspace item is user-visible regardless of worker tier —
  // moksy's identity model is explicitly dual: every user has a customer
  // track, AND can hold one or more worker tracks. A 'designer' is also
  // a customer (they can buy reviews, run evaluations, own projects).
  // Projects + AI requirements stay on every sidebar (operators inspect
  // them during support sessions). The customer-only surfaces below
  // use USER_TRACK_ROLES so the operator sidebar stays focused.
  // Customer landing console — the user-track analogue of OperatorConsole.
  // Hidden from operator-group accounts (they have OperatorConsole instead).
  //
  // Ordered by how often a customer reaches for it: land → work → talk →
  // outcomes. The reviewer-hat surfaces are grouped rather than interleaved,
  // because "I'm here to review someone else's spec" is a different job from
  // everything above it.
  { id: 'ws-home',              section: SECTION.WORKSPACE,   label: 'Home',              path: '/home',                    componentId: 'CustomerHome',    roles: USER_TRACK_ROLES, icon: 'home' },
  { id: 'ws-projects',          section: SECTION.WORKSPACE,   label: 'Projects',          path: '/projects',                componentId: 'ProjectList',     roles: ALL_AUTH_ROLES, icon: 'folder' },
  { id: 'ws-requirements',      section: SECTION.WORKSPACE,   label: 'Briefing',          path: '/requirements/:id',        componentId: 'RequirementsAI',  roles: ALL_AUTH_ROLES, icon: 'chat' },
  // Unified customer<->reviewer conversation inbox. Route is registered
  // as a static foundational route (routes.jsx), so this catalog row is
  // nav-only (its path is filtered out of dynamic route-building).
  // The job pool: real job posts the team runs through Moksy. Shown only to a
  // sourcing team (Sidebar reads `sourcingTeam` against /sourcing/access).
  { id: 'ws-sourcing',          section: SECTION.WORKSPACE,   label: 'Job pool',          path: '/sourcing',                componentId: 'SourcingPool',    roles: ALL_AUTH_ROLES, sourcingTeam: true, icon: 'briefcase' },
  { id: 'ws-messages',          section: SECTION.WORKSPACE,   label: 'Messages',          path: '/messages',                componentId: 'MessagesInbox',   roles: ALL_AUTH_ROLES, icon: 'messageSquare' },
  // Top-level "all my review requests" view. Project-scoped review
  // pages still exist (/requirements/v2/:id/reviews), but customers
  // also need a one-stop list across every project they own.
  { id: 'ws-my-reviews',        section: SECTION.WORKSPACE,   label: 'Reviews',           path: '/reviews',               componentId: 'MyReviews',         roles: USER_TRACK_ROLES, icon: 'clipboardCheck' },
  // The customer's own downloads (`/downloads/my-history` — the caller's rows
  // only). Customers only: it lists what THIS account downloaded, which for a
  // staff account is nothing; staff reach downloads through "Downloads admin".
  { id: 'ws-downloads',         section: SECTION.WORKSPACE,   label: 'Exports',           path: '/downloads',               componentId: 'DownloadsHome',   roles: USER_TRACK_ROLES, icon: 'download' },
  // Pillars (paid pillar-pack top-ups) and the concierge request list are
  // paid-tier surfaces — hidden from free customers so their sidebar shows
  // only what they need + have. `minTier` is enforced in core/Sidebar.jsx
  // against the canonical taxonomy (src/config/plans.js); free users get an
  // upgrade prompt on Home instead of dead paid links. Tier gates are only
  // read off TOP-LEVEL rows, so these two must never be nested.
  { id: 'ws-pillars',           section: SECTION.ACCOUNT,     label: 'Areas',             path: '/billing/areas',           componentId: 'BillingPillars',  roles: USER_TRACK_ROLES, minTier: 'paid_any', icon: 'columns' },
  { id: 'ws-my-concierge',      section: SECTION.WORKSPACE,   label: 'Sprints',          path: '/concierge/my-requests', componentId: 'MyConciergeRequests', roles: USER_TRACK_ROLES, minTier: 'paid_any', icon: 'headset' },
  // Reviewer hat. Both rows are worker-track-only and always travel together,
  // so they're one group ("switch to reviewer" = open this). Operators have a
  // dedicated Review pipeline page, so this stays hidden for them.
  {
    id: 'ws-reviewer-hat', section: SECTION.WORKSPACE, label: 'Reviewer', roles: WORKER_TRACK_ROLES, icon: 'userCheck',
    children: [
      { id: 'ws-reviewer',     label: 'Desk',                path: '/reviewer',       componentId: 'ReviewerDashboard', icon: 'gauge' },
      { id: 'ws-review-queue', label: 'Queue',               path: '/reviewer/queue', componentId: 'ExpertReviewQueue', icon: 'listChecks' },
      // The requirements ledger — what a review actually earned, and what
      // is drawable. Five routes and a tested service had no client at
      // all, so this was unreachable rather than unbuilt.
      { id: 'ws-reviewer-standing', label: 'Standing', path: '/reviewer/standing', componentId: 'MyStandingPage', icon: 'award' },
      { id: 'ws-reviewer-earnings', label: 'Earnings', path: '/reviewer/earnings', componentId: 'ReviewerEarningsPage', icon: 'coins' },
      // 🎖️ The reviewer's own plan — deliberately NOT under Billing, which
      // is the same person's customer subscription. Two subscriptions on
      // one screen is how somebody cancels the wrong one.
      { id: 'ws-reviewer-plan', label: 'Membership', path: '/reviewer/plan', componentId: 'ReviewerPlanPage', icon: 'award' },
      // 🔎 The public directory. It has existed at /reviewers, routed and
      // rendering, with nothing anywhere pointing at it — so the only way
      // to reach the page listing every reviewer was to know the URL.
      // A ladder nobody can see is not a ladder.
      { id: 'ws-reviewer-directory', label: 'Directory', path: '/reviewers', componentId: 'ReviewerDirectoryPage', icon: 'users' },
      // The accreditation rail stays reachable after approval — it carries
      // levels, skills and the admin thread, not just the application. It sits
      // HERE for approved reviewers rather than under "Become a reviewer",
      // which would be inviting them to become something they already are.
      { id: 'ws-accreditation-rev', label: 'Accreditation', path: '/reviewer/application', componentId: 'ApplicationRailPage', icon: 'route' }
    ]
  },
  // Non-reviewer customers get a discoverable path INTO reviewing. Same
  // /reviewer route (it renders the apply form until approved); the `?apply=1`
  // suffix only keeps the catalog's path-dedupe from collapsing it into the
  // "Reviewer dashboard" row above. Shown only to applicants so the two
  // never double-list.
  //
  // ONE group with two children rather than two unrelated top-level rows.
  // "Become a reviewer" and "Application progress" sat side by side as peers,
  // which read as two separate things to do — and gave no hint that the second
  // is where you go AFTER the first. Applying and then tracking the
  // application is one path, so it is one heading.
  {
    id: 'ws-reviewer-path', section: SECTION.WORKSPACE, label: 'Accreditation',
    roles: APPLICANT_ROLES, icon: 'userPlus',
    children: [
      { id: 'ws-become-reviewer', label: 'Apply',                path: '/reviewer?apply=1',     icon: 'userPlus' },
      { id: 'ws-accreditation',   label: 'Progress',             path: '/reviewer/application', componentId: 'ApplicationRailPage', icon: 'route' }
    ]
  },
  // 🧭 Application progress. The page and its route have existed at
  // /reviewer/application for a while — what was missing was any way to
  // REACH it. With no catalog row, an applicant's only reviewer surface
  // was /reviewer, which shows a bare status word and nothing about where
  // they actually stand or how to ask about it.
  //
  // USER_TRACK_ROLES rather than APPLICANT_ROLES: before accreditation
  // this answers "where am I in the funnel", and after it still carries
  // the rail and the thread. Top-level rather than a child of the
  // reviewer-hat group, since it is about the person's own standing
  // rather than the review workspace.


  // ── Progression (customer-side only) ─────────────────────────────
  // Operator accounts don't earn MOX or climb the customer ladder via
  // their staff identity. They can still inspect a user's wallet from
  // the user drawer in IdentityConsole.
  // The ladder is the REVIEWER progression rail — levels, accreditation,
  // eligibility for higher-value jobs. A customer has no position on it and
  // nothing they can do moves them along it, so it was a permanent sidebar row
  // leading to a page about somebody else's career. Worker-track only, matching
  // the reviewer group above.
  { id: 'prog-ladder',          section: SECTION.PROGRESSION, label: 'Ladder',            path: '/ladder',                  componentId: 'MyLadder',        roles: WORKER_TRACK_ROLES, icon: 'trendingUp' },
  { id: 'prog-wallet',          section: SECTION.ACCOUNT,     label: 'Wallet',            path: '/wallet',                  componentId: 'MyMoxWallet',     roles: USER_TRACK_ROLES, icon: 'wallet' },
  { id: 'prog-earn',            section: SECTION.ACCOUNT,     label: 'What earns',        path: '/earn',                    componentId: 'EarnGuidePage',   roles: USER_TRACK_ROLES, icon: 'award' },
  // 🏛️ The Commons window: what earned MOX and verified MXP become.
  { id: 'commons-window',       section: SECTION.ACCOUNT,     label: 'The Commons',       path: '/commons/window',          componentId: 'CommonsWindowPage', roles: USER_TRACK_ROLES, icon: 'coins' },
  { id: 'commons-governance',   section: SECTION.ACCOUNT,     label: 'Votes & council',   path: '/commons/governance',      componentId: 'CommonsGovernancePage', roles: USER_TRACK_ROLES, icon: 'coins' },

  // ── Account ──────────────────────────────────────────────────────
  // Profile stays on the operator sidebar (they edit their own staff
  // profile). Billing + Purchases + Transactions are customer-only —
  // operator staff accounts have no plan to manage. Ordered identity →
  // money → preferences.
  { id: 'acct-profile',         section: SECTION.ACCOUNT,     label: 'Profile',           path: '/profile',                 componentId: 'UserProfile',     roles: ALL_AUTH_ROLES, icon: 'user' },
  // Billing hub — single entry point for subscription, invoices,
  // saved cards, billing address, transactions. Replaces the older
  // standalone "Subscription" sidebar item.
  { id: 'acct-billing',         section: SECTION.ACCOUNT,     label: 'Billing',           path: '/billing',                 componentId: 'BillingHub',      roles: USER_TRACK_ROLES, icon: 'creditCard' },
  // Customer-facing personal transactions. Declared here, in the section it
  // renders in — it used to sit at the bottom of the Commerce block carrying
  // `section: ACCOUNT`, which read as an admin row to anyone scanning this file.
  { id: 'acct-purchases',       section: SECTION.ACCOUNT,     label: 'Purchases',         path: '/purchases',               componentId: 'PurchasesWorkspace', roles: USER_TRACK_ROLES, icon: 'shoppingBag' },
  // Per-user notification preferences — channel toggles, digest opt-in,
  // event overrides. Backed by /notifications/preferences in the
  // notifications service. Lives in Account next to Profile so users
  // who think "settings" naturally find it there.
  { id: 'acct-notif-prefs',     section: SECTION.ACCOUNT,     label: 'Notifications',            path: '/settings/notifications', componentId: 'NotificationPreferences', roles: ALL_AUTH_ROLES, icon: 'bell' },

  // ── People (owner-group) ─────────────────────────────────────────
  // Empty on purpose. Auth, Permissions and Captcha now live in the
  // "People & access" group of the Operator console (GC-094) — one heading
  // for people, rather than two whose difference only this file knew.

  // ── Build (admin) ────────────────────────────────────────────────
  { id: 'admin-projects',       section: SECTION.BUILD,       label: 'Projects',          path: '/admin/projects/control',  componentId: 'ProjectControl',  roles: BY_DESK, icon: 'folderCog' },
  { id: 'admin-requirements',   section: SECTION.BUILD,       label: 'Requirements',      path: '/admin/requirements/control', componentId: 'RequirementsControl', roles: BY_DESK, icon: 'fileText' },
  { id: 'admin-preview',        section: SECTION.BUILD,       label: 'Preview',           path: '/admin/preview/control',   componentId: 'PreviewControl',  roles: BY_DESK, icon: 'eye' },
  {
    id: 'admin-registry', section: SECTION.BUILD, label: 'Asset registry', roles: BY_DESK, icon: 'archive',
    children: [
      { id: 'reg-overview',  label: 'Overview',  path: '/admin/registry/control',           componentId: 'RegistryHome', icon: 'gauge' },
      { id: 'reg-catalog',   label: 'Catalogue',   path: '/admin/registry/control/catalog',    icon: 'list' },
      { id: 'reg-templates', label: 'Templates', path: '/admin/registry/control/templates',  icon: 'layoutTemplate' },
      { id: 'reg-trending',  label: 'Trending',  path: '/admin/registry/control/trending',   icon: 'trendingUp' },
      { id: 'reg-conflicts', label: 'Conflicts', path: '/admin/registry/control/conflicts',  icon: 'alertOctagon' }
    ]
  },

  // ── Knowledge (admin) ────────────────────────────────────────────
  {
    id: 'admin-dictionary', section: SECTION.KNOWLEDGE, label: 'Dictionary', roles: BY_DESK, icon: 'bookOpen',
    children: [
      { id: 'dict-overview',  label: 'Overview',   path: '/admin/dictionary/control',            icon: 'gauge' },
      { id: 'dict-types',     label: 'Data types', path: '/admin/dictionary/control/data-types',  icon: 'braces' },
      { id: 'dict-templates', label: 'Templates',  path: '/admin/dictionary/control/templates',   icon: 'layoutTemplate' },
      { id: 'dict-settings',  label: 'Settings',   path: '/admin/dictionary/control/settings',    icon: 'settings' }
    ]
  },
  {
    id: 'admin-storage', section: SECTION.KNOWLEDGE, label: 'Storage', roles: BY_DESK, icon: 'database',
    children: [
      { id: 'stg-overview',  label: 'Overview',  path: '/admin/storage/control',           componentId: 'StorageHome', icon: 'gauge' },
      { id: 'stg-files',     label: 'Files',     path: '/admin/storage/control/files',     icon: 'list' },
      { id: 'stg-flagged',   label: 'Flagged',   path: '/admin/storage/control/flagged',   icon: 'flag' },
      { id: 'stg-providers', label: 'Providers', path: '/admin/storage/control/providers', icon: 'plug' },
      // The only upload / scan / purge / migration screen — built and never
      // linked (AIF-STAFF-134). Generated route; desk from /admin/storage/control.
      { id: 'stg-operations', label: 'Operations', path: '/admin/storage/control/operations', componentId: 'StorageControl', icon: 'cog' }
    ]
  },

  // ── AI Platform (admin) ──────────────────────────────────────────
  {
    id: 'admin-ai-llm', section: SECTION.AI_PLATFORM, label: 'AI models', roles: BY_DESK, icon: 'sparkles',
    children: [
      { id: 'llm-overview',    label: 'Overview',        path: '/admin/ai-llm/control',                 componentId: 'AiLlmHome', icon: 'gauge' },
      { id: 'llm-active',      label: 'Active provider', path: '/admin/ai-llm/control/active-provider', icon: 'zap' },
      { id: 'llm-providers',   label: 'Providers',       path: '/admin/ai-llm/control/providers',       icon: 'plug' },
      { id: 'llm-models',      label: 'Models',          path: '/admin/ai-llm/control/models',          icon: 'boxes' },
      { id: 'llm-routing',     label: 'Routing',         path: '/admin/ai-llm/control/routing',         icon: 'route' },
      { id: 'llm-costs',       label: 'Costs',           path: '/admin/ai-llm/control/costs',           icon: 'coins' },
      { id: 'llm-performance', label: 'Performance',     path: '/admin/ai-llm/control/performance',     icon: 'activity' }
    ]
  },
  {
    id: 'admin-ai-memory', section: SECTION.AI_PLATFORM, label: 'AI memory', roles: BY_DESK, icon: 'brain',
    children: [
      { id: 'mem-overview',   label: 'Overview',   path: '/admin/ai-memory/control',            componentId: 'AiMemoryHome', icon: 'gauge' },
      { id: 'mem-namespaces', label: 'Namespaces', path: '/admin/ai-memory/control/namespaces', icon: 'boxes' },
      { id: 'mem-providers',  label: 'Providers',  path: '/admin/ai-memory/control/providers',  icon: 'plug' }
    ]
  },
  {
    id: 'admin-ai-policy', section: SECTION.AI_PLATFORM, label: 'AI policy', roles: BY_DESK, icon: 'scale',
    children: [
      { id: 'pol-overview', label: 'Overview',        path: '/admin/ai-policy/control',                  componentId: 'AiPolicyHome', icon: 'gauge' },
      { id: 'pol-routing',  label: 'Routing rules',   path: '/admin/ai-policy/control/routing-rules',    icon: 'route' },
      { id: 'pol-denylist', label: 'Denylist',        path: '/admin/ai-policy/control/denylist',         icon: 'ban' },
      { id: 'pol-scrub',    label: 'Scrub rules',     path: '/admin/ai-policy/control/scrub-rules',      icon: 'eraser' },
      { id: 'pol-decisions',label: 'Audit decisions', path: '/admin/ai-policy/control/audit-decisions',  icon: 'gavel' },
      { id: 'pol-versions', label: 'Versions',        path: '/admin/ai-policy/control/versions',         icon: 'gitCompare' }
    ]
  },

  // ── Operations (admin) ───────────────────────────────────────────
  // Ordered business pipeline → observability → messaging → runtime.
  //
  // Every service here follows the SAME shape as Templates and Payments: the
  // service row is a group, "Overview" is its /control root, and each of its
  // sub-pages is a child with its own glyph. Those sub-pages have always
  // existed as routes (JobsList entity="queues", EventsList entity="webhooks",
  // …) but were referenced from NOWHERE in the UI — not the sidebar, not an
  // in-page tab — so the only way to reach 77 real admin screens was to type
  // the URL. That is what this section fixes.
  { id: 'admin-concierge',      section: SECTION.OPERATIONS,  label: 'Concierge pipeline',path: '/admin/concierge',         componentId: 'ConciergeQueue',  roles: BY_DESK, icon: 'headset' },
  // Social login. Listed so the page is reachable — an admin screen nothing
  // links to is one nobody finds, which is how the auth service ended up with
  // a complete provider API and no way to use it.
  { id: 'admin-auth-providers', section: SECTION.OPERATIONS,  label: 'Social login',      path: '/admin/auth-providers',    componentId: 'AuthProviders',   roles: BY_DESK, icon: 'shieldCheck' },
  {
    id: 'admin-audit', section: SECTION.OPERATIONS, label: 'Audit', roles: BY_DESK, icon: 'clipboardList',
    children: [
      { id: 'aud-overview',   label: 'Overview',   path: '/admin/audit/control',            componentId: 'AuditHome', icon: 'gauge' },
      { id: 'aud-entries',    label: 'Entries',    path: '/admin/audit/control/entries',    icon: 'list' },
      { id: 'aud-suspicious', label: 'Suspicious', path: '/admin/audit/control/suspicious', icon: 'alertOctagon' },
      { id: 'aud-integrity',  label: 'Integrity',  path: '/admin/audit/control/integrity',  icon: 'fingerprint' }
    ]
  },
  {
    id: 'admin-events', section: SECTION.OPERATIONS, label: 'Events', roles: BY_DESK, icon: 'activity',
    children: [
      { id: 'evt-overview', label: 'Overview',      path: '/admin/events/control',               componentId: 'EventsHome', icon: 'gauge' },
      { id: 'evt-subs',     label: 'Subscriptions', path: '/admin/events/control/subscriptions', icon: 'listChecks' },
      { id: 'evt-webhooks', label: 'Webhooks',      path: '/admin/events/control/webhooks',      icon: 'webhook' },
      { id: 'evt-types',    label: 'Event types',   path: '/admin/events/control/types',         icon: 'braces' },
      { id: 'evt-failed',   label: 'Failed',        path: '/admin/events/control/failed',        icon: 'xCircle' }
    ]
  },
  {
    id: 'admin-notifications', section: SECTION.OPERATIONS, label: 'Notifications', roles: BY_DESK, icon: 'bell',
    children: [
      { id: 'ntf-overview',   label: 'Overview',   path: '/admin/notifications/control',            componentId: 'NotificationsHome', icon: 'gauge' },
      { id: 'ntf-inbox',      label: 'Inbox',      path: '/admin/notifications/control/inbox',      icon: 'inbox' },
      { id: 'ntf-broadcasts', label: 'Broadcasts', path: '/admin/notifications/control/broadcasts', icon: 'radio' },
      { id: 'ntf-logs',       label: 'Logs',       path: '/admin/notifications/control/logs',       icon: 'list' },
      { id: 'ntf-failures',   label: 'Failures',   path: '/admin/notifications/control/failures',   icon: 'xCircle' },
      { id: 'ntf-mappings',   label: 'Mappings',   path: '/admin/notifications/control/mappings',   icon: 'route' }
    ]
  },
  {
    id: 'admin-comms', section: SECTION.OPERATIONS, label: 'Communications', roles: BY_DESK, icon: 'mail',
    children: [
      { id: 'com-overview',   label: 'Overview',   path: '/admin/communications/control',            componentId: 'CommunicationsHome', icon: 'gauge' },
      { id: 'com-threads',    label: 'Threads',    path: '/admin/communications/control/threads',    icon: 'messageSquare' },
      { id: 'com-templates',  label: 'Templates',  path: '/admin/communications/control/templates',  icon: 'layoutTemplate' },
      { id: 'com-broadcasts', label: 'Broadcasts', path: '/admin/communications/control/broadcasts', icon: 'radio' },
      { id: 'com-providers',  label: 'Providers',  path: '/admin/communications/control/providers',  icon: 'plug' },
      { id: 'com-settings',   label: 'Settings',   path: '/admin/communications/control/settings',   icon: 'settings' },
      { id: 'com-logs',       label: 'Logs',       path: '/admin/communications/control/logs',       icon: 'list' }
    ]
  },
  {
    id: 'admin-orchestrator', section: SECTION.OPERATIONS, label: 'Orchestrator', roles: BY_DESK, icon: 'gitBranch',
    children: [
      { id: 'orc-overview',   label: 'Overview',   path: '/admin/orchestrator/control',            componentId: 'OrchestratorHome', icon: 'gauge' },
      { id: 'orc-workflows',  label: 'Workflows',  path: '/admin/orchestrator/control/workflows',  icon: 'workflow' },
      { id: 'orc-strategies', label: 'Strategies', path: '/admin/orchestrator/control/strategies', icon: 'route' },
      { id: 'orc-traces',     label: 'Traces',     path: '/admin/orchestrator/control/traces',     icon: 'history' }
    ]
  },
  {
    id: 'admin-jobs', section: SECTION.OPERATIONS, label: 'Background jobs', roles: BY_DESK, icon: 'cog',
    children: [
      { id: 'job-overview',  label: 'Overview',  path: '/admin/jobs/control',           componentId: 'JobsHome', icon: 'gauge' },
      { id: 'job-mine',      label: 'My jobs',   path: '/admin/jobs/control/mine',      icon: 'list' },
      { id: 'job-failed',    label: 'Failed',    path: '/admin/jobs/control/failed',    icon: 'xCircle' },
      { id: 'job-queues',    label: 'Queues',    path: '/admin/jobs/control/queues',    icon: 'stack' },
      { id: 'job-workers',   label: 'Workers',   path: '/admin/jobs/control/workers',   icon: 'cpu' },
      { id: 'job-schedules', label: 'Schedules', path: '/admin/jobs/control/schedules', icon: 'calendarClock' }
    ]
  },
  // Admin-side downloads (signed-link audit). Distinct from the customer
  // "Exports" row in Workspace, which is /downloads. Was absent from
  // the sidebar entirely, so its two sub-pages were unreachable.
  {
    id: 'admin-downloads', section: SECTION.OPERATIONS, label: 'Downloads admin', roles: BY_DESK, icon: 'download',
    children: [
      { id: 'dl-overview', label: 'Overview',     path: '/admin/downloads/control',              icon: 'gauge' },
      { id: 'dl-history',  label: 'History',      path: '/admin/downloads/control/history',      icon: 'history' },
      { id: 'dl-links',    label: 'Active links', path: '/admin/downloads/control/active-links', icon: 'link' }
    ]
  },
  { id: 'admin-gateway',        section: SECTION.OPERATIONS,  label: 'API gateway',       path: '/admin/gateway/control',   componentId: 'GatewayControl',  roles: BY_DESK, icon: 'server' },

  // ── Commerce (admin) ─────────────────────────────────────────────
  // The Payments service exposes a tab-style set of sub-pages under
  // /admin/payments/*, sharing one page chrome. Those five rows used to be
  // flat and carried a "Payments · " label prefix to fake the hierarchy the
  // sidebar already supports natively — they're a real group now, so the
  // labels are just the page names and the group header does the rest.
  {
    id: 'admin-payments-group', section: SECTION.COMMERCE, label: 'Payments', roles: BY_DESK, icon: 'creditCard',
    children: [
      { id: 'admin-payments',           label: 'Overview',     path: '/billing/payments',            componentId: 'PaymentsHome',         icon: 'gauge' },
      { id: 'admin-payments-tx',        label: 'Transactions', path: '/admin/payments/transactions', componentId: 'PaymentsTransactions', icon: 'receipt' },
      { id: 'admin-payments-disputes',  label: 'Disputes',     path: '/admin/payments/disputes',     componentId: 'PaymentsDisputes',     icon: 'alertTriangle' },
      { id: 'admin-payments-providers', label: 'Providers',    path: '/admin/payments/providers',    componentId: 'PaymentsProviders',    icon: 'plug' },
      // Stripe credential configurator. Read-only for most operator
      // roles; mutation requires super-admin (gateway-enforced).
      { id: 'admin-payment-settings',   label: 'Stripe',       path: '/admin/payments/settings',     componentId: 'PaymentSettings',      icon: 'settings' },
      // These three live under the OTHER payments admin surface
      // (/admin/payments/control/*, the generic 3-screen pattern) and have no
      // equivalent among the bespoke pages above — so they were unreachable.
      // Surfaced here rather than as a second "Payments" menu; see the note at
      // the top of this section about the two parallel payments surfaces.
      { id: 'pay-invoices', label: 'Invoices',        path: '/admin/payments/control/invoices', icon: 'banknote' },
      { id: 'pay-methods',  label: 'Payment methods', path: '/admin/payments/control/methods',  icon: 'wallet' },
      { id: 'pay-products', label: 'Products',        path: '/admin/payments/control/products', icon: 'package' }
    ]
  },
  // Subscriptions service — SubscriptionsHome plus five list screens. Had no
  // sidebar row of any kind, so the whole service was URL-only.
  {
    id: 'admin-subscriptions', section: SECTION.COMMERCE, label: 'Subscriptions', roles: BY_DESK, icon: 'receipt',
    children: [
      { id: 'sub-overview',    label: 'Overview',       path: '/admin/subscriptions/control',                icon: 'gauge' },
      { id: 'sub-plans',       label: 'Plans',          path: '/admin/subscriptions/control/plans',          icon: 'layers' },
      { id: 'sub-history',     label: 'History',        path: '/admin/subscriptions/control/history',        icon: 'history' },
      { id: 'sub-periods',     label: 'Periods',        path: '/admin/subscriptions/control/periods',        icon: 'calendar' },
      { id: 'sub-auths',       label: 'Authorisations', path: '/admin/subscriptions/control/authorizations', icon: 'shieldCheck' },
      { id: 'sub-unreconciled',label: 'Unreconciled',   path: '/admin/subscriptions/control/unreconciled',   icon: 'alertOctagon' }
    ]
  },
  { id: 'admin-billing',           section: SECTION.COMMERCE, label: 'Billing admin',           path: '/admin/billing/control',           componentId: 'BillingAdmin',         roles: BY_DESK, icon: 'fileText' }
];

// A leaf takes the roles whose desks work its path. A group takes the union of
// its children, and each child keeps its own list so the sidebar can drop the
// rows its reader may not open — "Reviews & experts" is Enquiries for Support
// and the whole marketplace for Reviewer operations. `byDesk` marks the row so
// the dictionary overlay below cannot widen it.
const resolveByDesk = (node) => {
  if (Array.isArray(node.children)) {
    const children = node.children.map((child) => resolveByDesk(child));
    const held = new Set(children.flatMap((child) => child.roles));
    return { ...node, byDesk: true, roles: OWNER_ROLES.filter((r) => held.has(r)), children };
  }
  return { ...node, byDesk: true, roles: rolesForSurface(node.path) };
};

// Exported so the nav can be asserted as DATA rather than by grepping this
// file for path strings — a regex over the source passes just as happily on a
// row that has been commented out.
export const BASELINE_CATALOG_ITEMS = DECLARED_CATALOG_ITEMS.map((item) => (
  item.roles === BY_DESK ? resolveByDesk(item) : item
));

// Deep copy of a catalog subtree, so a dictionary override mutates the copy
// and never the module-level baseline (which would leak across renders).
const cloneNode = (node) => (
  Array.isArray(node.children)
    ? { ...node, children: node.children.map(cloneNode) }
    : { ...node }
);

// Index every node in the tree — nested ones included — by componentId and by
// path, so a dictionary override lands ON the existing row wherever it sits.
// Indexing only the top level (the old behaviour) meant an override for a
// nested row (say Review pipeline, now under "Reviews & experts") matched
// nothing and got APPENDED as a second, ungrouped copy of the same page.
const indexTree = (nodes, byComponentId, byPath) => {
  for (const node of nodes) {
    if (node.componentId && !byComponentId.has(node.componentId)) byComponentId.set(node.componentId, node);
    if (node.path && !byPath.has(node.path)) byPath.set(node.path, node);
    if (Array.isArray(node.children)) indexTree(node.children, byComponentId, byPath);
  }
};

export const useDynamicCatalog = () => {
  // Fetches navigation.json from Service #4 (dictionary.js).
  // Dictionary entries can OVERRIDE labels/roles on baseline items but
  // cannot create dead routes — the baseline is canonical.
  const nav = useLanguage('navigation');
  return useMemo(() => {
    const items = Array.isArray(nav?.items) ? nav.items : [];
    const roots = BASELINE_CATALOG_ITEMS.map(cloneNode);
    const byComponentId = new Map();
    const byPath = new Map();
    indexTree(roots, byComponentId, byPath);

    // Dictionary navigation may override labels. Roles are MERGED
    // (union of baseline + dictionary), never replaced — so a dictionary
    // entry with narrower roles can't accidentally hide an item from a
    // user role the baseline says is allowed. This is what was hiding
    // Projects / AI requirements from the `designer` role: the
    // dictionary's pre-progression nav.json had `roles: ['user', 'admin']`
    // and was overwriting the baseline's wider list.
    const dropped = [];
    for (const item of items) {
      if (!item?.path) continue;
      const target = (item.componentId ? byComponentId.get(item.componentId) : null) || byPath.get(item.path);
      if (target) {
        const baseRoles = Array.isArray(target.roles) ? target.roles : [];
        // An operator row's roles come from the desk table, which the gateway
        // enforces too. A dictionary row may still retitle it, but a union
        // here would let a stale `roles: [..., 'support']` in navigation.json
        // put a link in front of somebody the door then refuses.
        const itemRoles = target.byDesk ? [] : (Array.isArray(item.roles) ? item.roles : []);
        // Mutate the node in place so nested rows keep their position in the
        // tree. Baseline path / section / children / icon win, so a dictionary
        // edit can retitle a row but can't re-parent it, strip its glyph, or
        // point it at a dead route.
        Object.assign(target, {
          ...item,
          path: target.path,
          section: target.section || item.section || null,
          roles: [...new Set([...baseRoles, ...itemRoles])],
          icon: target.icon || item.icon,
          ...(Array.isArray(target.children) ? { children: target.children } : {})
        });
        continue;
      }
      // ⛔ DROPPED, deliberately — and this is a behaviour change.
      //
      // This used to `appended.push(...)` any dictionary row that matched
      // no baseline item, which directly contradicts the contract stated
      // above it: "Dictionary entries can OVERRIDE labels/roles on
      // baseline items but cannot create dead routes — the baseline is
      // canonical." Appending is exactly creating one.
      //
      // The visible symptom: a stale dictionary row for "Overview → /"
      // survived long after the baseline stopped declaring it, so the
      // sidebar carried a phantom entry that duplicated the "+ New
      // project" button, sat outside every section, and could not be
      // removed from the code because it was never in the code. A row
      // nobody can find is a row nobody can fix.
      //
      // Dropping unmatched rows makes the baseline genuinely canonical:
      // to add a nav entry you add it here, where its route, section,
      // icon and roles are all declared together and can be reviewed.
      dropped.push(item.path);
    }

    if (dropped.length && typeof console !== 'undefined') {
      // Named rather than silent, so a legitimate dictionary row that
      // stopped appearing is debuggable instead of mysterious.
      console.warn(
        '[nav] dictionary rows ignored — no baseline item declares these paths:',
        dropped.join(', ')
      );
    }

    return roots;
  }, [nav?.items]);
};
